class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
      if (typeof window.initCartDrawerUpsell === 'function') {
        window.initCartDrawerUpsell(this, { resetClosed: true });
      }
      this.fitSingleLineTexts();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        this.fitSingleLineTexts();
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  patchContents(html) {
    if (!html) return false;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextRoot = doc.querySelector('cart-drawer');

    const nextEmptyState = doc.querySelector('.drawer__inner-empty');
    const currentEmptyState = this.querySelector('.drawer__inner-empty');
    if (Boolean(nextEmptyState) !== Boolean(currentEmptyState)) {
      return false;
    }

    const selectors = [
      '.cart-promo-banner',
      '.cart-drawer__form',
      '[data-cart-gift-boxes]',
      '[data-cart-drawer-upsell]',
      '.drawer__footer',
    ];

    const replacements = [];
    for (const selector of selectors) {
      const target = this.querySelector(selector);
      const next = doc.querySelector(selector);
      if (!target && next) return false;
      replacements.push({ target, next });
    }

    replacements.forEach(({ target, next }) => {
      if (target && next) {
        target.replaceWith(next);
      } else if (target && !next) {
        target.remove();
      }
    });

    if (nextRoot) {
      this.classList.toggle('is-empty', nextRoot.classList.contains('is-empty'));
    }

    const items = this.querySelector('cart-drawer-items');
    const nextItems = doc.querySelector('cart-drawer-items');
    if (items && nextItems) {
      items.className = nextItems.className;
    }

    return true;
  }

  replaceDrawer(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const current = document.querySelector('#CartDrawer');
    const next = doc.querySelector('#CartDrawer');
    const nextRoot = doc.querySelector('cart-drawer');

    if (current && next) {
      current.innerHTML = next.innerHTML;
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    }

    if (nextRoot) {
      this.classList.toggle('is-empty', nextRoot.classList.contains('is-empty'));
    }

    this.observeSingleLineFit();
    this.fitSingleLineTexts();
  }

  fitSingleLineTexts() {
    const root = this.querySelector('.drawer__inner') || this;
    const promoNodes = root.querySelectorAll('.cart-promo-banner__text, .cart-promo-banner__hint');
    const trust = root.querySelector('.cart-drawer__trust');
    const trustItems = trust ? trust.querySelectorAll('.cart-drawer__trust-item') : [];

    const clearFit = () => {
      promoNodes.forEach((el) => el.style.removeProperty('font-size'));
      trustItems.forEach((item) => item.style.removeProperty('font-size'));
    };

    if (window.innerWidth >= 390) {
      clearFit();
      return;
    }

    const contentWidth = (el) => {
      const range = document.createRange();
      range.selectNodeContents(el);
      return range.getBoundingClientRect().width;
    };
    const shrink = (el, minSize = 10) => {
      if (!el) return;
      el.style.removeProperty('font-size');
      let size = parseFloat(getComputedStyle(el).fontSize);
      let guard = 40;
      while (guard && contentWidth(el) > el.clientWidth - 2 && size > minSize) {
        guard -= 1;
        size = Math.max(minSize, size - 0.4);
        el.style.fontSize = `${size}px`;
      }
    };

    promoNodes.forEach((el) => shrink(el));

    if (trust && trustItems.length) {
      trustItems.forEach((item) => item.style.removeProperty('font-size'));
      const sample = trustItems[0];
      let size = parseFloat(getComputedStyle(sample).fontSize);
      let guard = 40;
      while (guard && trust.scrollWidth > trust.clientWidth + 0.5 && size > 10) {
        guard -= 1;
        size = Math.max(10, size - 0.4);
        trustItems.forEach((item) => {
          item.style.fontSize = `${size}px`;
        });
      }
    }
  }

  connectedCallback() {
    if (this._singleLineFitBound) return;
    this._singleLineFitBound = this.fitSingleLineTexts.bind(this);
    this._singleLineFitObserver = new ResizeObserver(() => {
      if (!this.classList.contains('active')) return;
      this.fitSingleLineTexts();
    });
    this.observeSingleLineFit();
    window.addEventListener('resize', this._singleLineFitBound, { passive: true });
  }

  observeSingleLineFit() {
    if (!this._singleLineFitObserver) return;
    this._singleLineFitObserver.disconnect();
    const inner = this.querySelector('.drawer__inner');
    if (inner) this._singleLineFitObserver.observe(inner);
  }

  initDrawerWidgets() {
    if (typeof window.initCartDrawerUpsell === 'function') {
      window.initCartDrawerUpsell();
    }
    if (typeof window.initCartPromoBanner === 'function') {
      window.initCartPromoBanner();
    }
    this.fitSingleLineTexts();
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;

    const html = parsedState.sections && parsedState.sections['cart-drawer'];
    if (!html || !this.patchContents(html)) {
      if (html) this.replaceDrawer(html);
    }

    const cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon && parsedState.sections && parsedState.sections['cart-icon-bubble']) {
      cartIcon.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble']);
    }

    this.initDrawerWidgets();

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
        patch: true,
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
